"use client";
import React, {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
    Box,
    Flex,
    GridItem,
    Text,
    Heading,
    Field,
    Fieldset,
    Stack,
    Input,
    Button,
    Spinner,
    SimpleGrid,
    Icon,
    VStack,
    Card,
    Image,
    Skeleton,
    AspectRatio,
    Portal,
    Select,
    createListCollection,
    ButtonGroup, HStack, List,
} from '@chakra-ui/react';
import {toaster} from "../ui/toaster";
import {FiDownload, FiMapPin, FiImage, FiVideo, FiInfo} from 'react-icons/fi';
import MapWithDraw from "../SatelliteImageryData/MapWithDraw";
import {useColorModeValue} from "../ui/color-mode";
import axios from 'axios';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Area,
    ResponsiveContainer
} from "recharts";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// TODO: implement list of previous satellite imagery queries (table, ranges, date of request, etc.)
// TODO: Implement in the backend uploading the images to aws s3, associated with the query

const baseUrl = process.env.REACT_APP_API_URL;
const baseUrlWs = process.env.REACT_APP_API_URL_WS;

const DemoSatelliteImagery = () => {
    const [applicant, setApplicant] = useState(null);
    const [isLoadingApplicant, setIsLoadingApplicant] = useState(true);
    const token = localStorage.getItem("token");
    const {applicant_id} = useParams();
    const cardBg = useColorModeValue('white', 'white');
    const [ndviData, setNdviData] = useState(null);
    const [ndviAnalysisText, setNdviAnalysisText] = useState("");
    const [loadingAnalysis, setLoadingAnalysis] = useState(false);
    const [polygonGeoJSON, setPolygonGeoJSON] = useState(null);
    const [drawingMode, setDrawingMode] = useState(true);
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);
    const formatDate = (date) => date.toISOString().split("T")[0];
    const [startDate, setStartDate] = useState(formatDate(sixMonthsAgo));
    const [endDate, setEndDate] = useState(formatDate(today));
    const [quickRange, setQuickRange] = useState(["last_6_months"]);
    const [loadingPreviews, setLoadingPreviews] = useState(false);
    const [previewImages, setPreviewImages] = useState([]);
    const [imageLoadState, setImageLoadState] = useState({});
    const [imageCacheBust, setImageCacheBust] = useState({});

    const [mode, setMode] = useState(["NDVI (B8, B4)"]);
    const [customRedBand, setCustomRedBand] = useState(["B04"]);
    const [customGreenBand, setCustomGreenBand] = useState(["B03"]);
    const [customBlueBand, setCustomBlueBand] = useState(["B02"]);

    // const handleNDVIAnalysis = async () => {
    //     if (!applicant || !ndviData || ndviData.length === 0) {
    //         toaster.error({
    //             title: "Cannot perform analysis",
    //             description: "Missing applicant or NDVI data",
    //             status: "error",
    //             duration: 3000,
    //             isClosable: true,
    //         });
    //         return;
    //     }
    //
    //     try {
    //         setLoadingAnalysis(true);
    //
    //         const payload = {
    //             applicant,        // pass the full applicant object
    //             ndvi_data: ndviData  // pass the array of {timestamp, ndvi}
    //         };
    //
    //         const response = await axios.post(
    //             `${baseUrl}/satellite_imagery/ndvi-analysis/demo`,
    //             payload
    //         );
    //
    //         setNdviAnalysisText(response.data.analysis_text || "No analysis returned");
    //
    //     } catch (error) {
    //         console.error("NDVI analysis error:", error);
    //         toaster.error({
    //             title: "Error performing analysis",
    //             description: error.response?.data?.detail || error.message,
    //             status: "error",
    //             duration: 5000,
    //             isClosable: true,
    //         });
    //     } finally {
    //         setLoadingAnalysis(false);
    //     }
    // };

    const handleNDVIAnalysis = async () => {
        if (!applicant || !ndviData || ndviData.length === 0) {
            toaster.error({
                title: "Cannot perform analysis",
                description: "Missing applicant or NDVI data",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setNdviAnalysisText("");
        setLoadingAnalysis(true);

        const fullTextRef = {current: ""};

        try {
            const wsUrl = `${baseUrlWs}/satellite_imagery/ndvi-analysis/ws/demo`;
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                ws.send(JSON.stringify({
                    applicant,          // still sending it, even if backend ignores
                    ndvi_data: ndviData
                }));
            };

            ws.onmessage = (event) => {
                const data = JSON.parse(event.data);

                if (data.type === "chunk") {
                    fullTextRef.current += data.chunk;
                    setNdviAnalysisText(fullTextRef.current.trim());
                }

                if (data.status === "complete" || data.status === "error") {
                    setLoadingAnalysis(false);
                    ws.close();
                }
            };

            ws.onerror = (err) => {
                console.error("WebSocket error:", err);
                setLoadingAnalysis(false);
            };

        } catch (error) {
            console.error("NDVI analysis error:", error);
            setLoadingAnalysis(false);
        }
    };

    useEffect(() => {
        if (
            ndviData &&
            ndviData.length > 0 &&
            !loadingAnalysis &&
            !loadingPreviews
        ) {
            console.log("Auto-triggering NDVI analysis...");
            handleNDVIAnalysis();
        }

    }, [ndviData, loadingPreviews]);

    // const handleNDVIAnalysis = useCallback(async (currentNdvi, currentApplicant) => {
    //     // Basic guard: don't run if already loading or if text already exists
    //     if (!currentApplicant || !currentNdvi || currentNdvi.length === 0 || loadingAnalysis) {
    //         return;
    //     }
    //
    //     try {
    //         setLoadingAnalysis(true);
    //
    //         const payload = {
    //             applicant: currentApplicant,
    //             ndvi_data: currentNdvi
    //         };
    //
    //         const response = await axios.post(
    //             `${baseUrl}/satellite_imagery/ndvi-analysis/${currentApplicant.id}`,
    //             payload
    //         );
    //
    //         setNdviAnalysisText(response.data.analysis_text || "No analysis returned");
    //
    //     } catch (error) {
    //         console.error("NDVI analysis error:", error);
    //         // We use a silent console error here because the user didn't "click" anything,
    //         // but you can still use a toaster if you want them to know the auto-run failed.
    //     } finally {
    //         setLoadingAnalysis(false);
    //     }
    // }, [baseUrl, loadingAnalysis]);


    const handleQuickRangeChange = (value) => {
        const today = new Date()
        let newStartDate = null

        if (value === "last_month") {
            newStartDate = new Date()
            newStartDate.setMonth(today.getMonth() - 1)
        } else if (value === "last_3_months") {
            newStartDate = new Date()
            newStartDate.setMonth(today.getMonth() - 3)
        } else if (value === "last_6_months") {
            newStartDate = new Date()
            newStartDate.setMonth(today.getMonth() - 6)
        }

        if (newStartDate) {
            setStartDate(formatDate(newStartDate))
            setEndDate(formatDate(today))
        }
    }

    const allBands = [
        {label: "B01", value: "B01"},
        {label: "B02", value: "B02"},
        {label: "B03", value: "B03"},
        {label: "B04", value: "B04"},
        {label: "B05", value: "B05"},
        {label: "B06", value: "B06"},
        {label: "B07", value: "B07"},
        {label: "B08", value: "B08"},
        {label: "B8A", value: "B8A"},
        {label: "B11", value: "B11"},
        {label: "B12", value: "B12"},
    ];

    const visualizationModes = createListCollection({
        items: [
            // {label: "Natural Color (B4, B3, B2)", value: "Natural Color (B4, B3, B2)"},
            // {label: "Color Infrared (B8, B4, B3)", value: "Color Infrared (B8, B4, B3)"},
            // {label: "Agriculture (B11, B8, B2)", value: "Agriculture (B11, B8, B2)"},
            // {label: "Healthy Vegetation (B8, B11, B2)", value: "Healthy Vegetation (B8, B11, B2)"},
            {label: "NDVI (B8, B4)", value: "NDVI (B8, B4)"},
            // {label: "Custom (choose RGB)", value: "Custom (choose RGB)"},
        ],
    });

    // Utility: group images by month and week
    const groupImagesByMonthAndWeek = (images) => {
        const grouped = {};

        images.forEach(({url, timestamp}) => {
            const date = new Date(timestamp);

            const monthKey = date.toLocaleString("default", {month: "long", year: "numeric"});
            const weekOfMonth = Math.ceil(
                (date.getDate() + new Date(date.getFullYear(), date.getMonth(), 1).getDay()) / 7
            );
            const weekKey = `Week ${weekOfMonth}`;

            if (!grouped[monthKey]) grouped[monthKey] = {};
            if (!grouped[monthKey][weekKey]) grouped[monthKey][weekKey] = [];

            grouped[monthKey][weekKey].push({url, timestamp});
        });

        return grouped;
    };

    const quickRanges = createListCollection({
        items: [
            {label: "Last Month", value: "last_month"},
            {label: "Last 3 Months", value: "last_3_months"},
            {label: "Last 6 Months", value: "last_6_months"},
        ],
    })


    const bandCollection = createListCollection({items: allBands});


    const handlePolygonCreated = (newPolygon) => {
        if (newPolygon.layer && newPolygon.layer.getLatLngs) {
            const coords = newPolygon.layer.getLatLngs()[0].map((pt) => [pt.lng, pt.lat]);
            const geoJson = {
                type: "Polygon",
                coordinates: [coords.concat([coords[0]])]
            };
            setPolygonGeoJSON(geoJson);
            setDrawingMode(false);

            toaster.success({
                title: "Area selected",
                description: "Polygon drawn successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const startPreviewWebSocket = () => {
        if (!polygonGeoJSON) {
            toaster.error({
                title: "Cannot generate previews",
                description: "Please select an area first.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setPreviewImages([]);
        setNdviData(null);
        setLoadingPreviews(true);

        const geojsonStr = encodeURIComponent(JSON.stringify(polygonGeoJSON));
        const modeStr = encodeURIComponent(mode[0]);

        const wsUrl = `${baseUrlWs}/satellite_imagery/ws/preview-images/demo/?start_date=${startDate}&end_date=${endDate}&mode=${modeStr}&geojson=${geojsonStr}`;

        console.log("Connecting to WebSocket:", wsUrl);

        const ws = new WebSocket(wsUrl);

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);

            switch (data.status) {
                case "progress": {
                    if (data.url) {
                        // 👇 renders immediately
                        setPreviewImages(prev => [
                            ...prev,
                            {url: data.url, timestamp: data.timestamp}
                        ]);
                    }
                    break;
                }

                case "ndvi":
                    if (data.ndvi_timeseries) {
                        // Store in state
                        setNdviData(data.ndvi_timeseries);

                        // --- LOGGING NDVI DATA ---
                        console.log("NDVI timeseries received:");
                        console.table(data.ndvi_timeseries); // nice table format
                        // or just raw JSON
                        // console.log(JSON.stringify(data.ndvi_timeseries, null, 2));
                    }
                    break;

                case "complete": {
                    setLoadingPreviews(false);

                    toaster.success({
                        title: "Previews Generated",
                        description: "Successfully loaded preview images",
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                    break;
                }

                case "error": {
                    console.error("WS error:", data.detail);
                    setLoadingPreviews(false);

                    toaster.error({
                        title: "Error generating previews",
                        description: data.detail,
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                    });
                    break;
                }

                case "warning": {
                    console.warn("WS warning:", data.message);
                    break;
                }

                default:
                    break;
            }
        };

        ws.onerror = (err) => {
            console.error("WebSocket error:", err);
            setLoadingPreviews(false);
        };

        ws.onclose = () => {
            console.log("WebSocket closed");
        };

        // IMPORTANT: return cleanup if this is used in useEffect
        return () => ws.close();
    };


    // Initialize image load states when new preview images arrive
    useEffect(() => {
        if (previewImages.length > 0) {
            const initialStates = {};
            const initialRetries = {};
            const initialCacheBust = {};
            previewImages.forEach((src) => {
                initialStates[src] = 'loading';
                initialRetries[src] = 0;
                initialCacheBust[src] = 0;
            });

            setImageLoadState(initialStates);
            setImageCacheBust(initialCacheBust);
        } else {
            setImageLoadState({});
            setImageCacheBust({});
        }
    }, [previewImages]);


    useEffect(() => {
        const fetchApplicant = async () => {
            setIsLoadingApplicant(true);
            try {
                const response = await fetch(`${baseUrl}/applicant/dashboard/demo/`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                        app_origin: "caz",
                    },
                });
                const data = await response.json();

                if (data.meta?.polygon_data) {
                    setPolygonGeoJSON(data.meta.polygon_data);
                    setDrawingMode(false);
                }
                console.log('applicant_data = ', data)

                setApplicant(data);
            } catch (error) {
                console.error("Error fetching applicant:", error);
                toaster.error({
                    title: "Error",
                    description: "Failed to load applicant data",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setIsLoadingApplicant(false);
            }
        };

        fetchApplicant();
    }, [baseUrl]);

    return (
        <Box p={6} bgGradient="to-r" gradientFrom="gray.100" gradientTo="gray.100">
            <Flex justifyContent="space-between" alignItems="center" mb={8}>
                <VStack align="flex-start">
                    <Heading as="h1" size="xl">Satellite Imagery</Heading>
                </VStack>
            </Flex>

            <GridItem colSpan={6}>
                <Card.Root bg={cardBg} borderRadius="xl" mb={8}>
                    <Card.Header>
                        <Flex justify="space-between" align="center">
                            <Flex align="center">
                                <Icon as={FiMapPin} mr={2} color="gray.500"/>
                                <Heading size="md">Plot Location</Heading>
                            </Flex>
                        </Flex>

                    </Card.Header>
                    <Card.Body>
                        <Box bg="gray.100" borderRadius="lg" overflow="hidden" position="relative">
                            <MapWithDraw
                                geoJson={polygonGeoJSON || applicant?.meta?.polygon_data}
                                onPolygonCreated={handlePolygonCreated}
                                drawingMode={drawingMode}
                                zIndex={0}
                            />
                        </Box>
                    </Card.Body>
                </Card.Root>
            </GridItem>

            <Box bg="white" p={8} rounded="lg">

                <Fieldset.Root size="lg" maxW="md">
                    <Stack>
                        <Fieldset.Legend>Time Range and Visualization</Fieldset.Legend>
                        <Fieldset.HelperText>
                            Select the time window for imagery and the visualization mode.
                        </Fieldset.HelperText>
                    </Stack>

                    <Field.Root mb={4}>
                        <Field.Label>Quick Range</Field.Label>
                        <Select.Root
                            collection={quickRanges}
                            value={quickRange}
                            onValueChange={(e) => {
                                setQuickRange(e.value) // Correctly sets state to the array of selected values, e.g. ["last_month"]
                                handleQuickRangeChange(e.value[0]) // Correctly calls the date logic with the string value
                            }}
                        >
                            <Select.HiddenSelect/>
                            <Select.Control>
                                <Select.Trigger>
                                    <Select.ValueText placeholder="Select Quick Range"/>
                                </Select.Trigger>
                                <Select.IndicatorGroup>
                                    <Select.Indicator/>
                                </Select.IndicatorGroup>
                            </Select.Control>
                            <Portal>
                                <Select.Positioner>
                                    <Select.Content>
                                        {quickRanges.items.map((item) => (
                                            <Select.Item item={item} key={item.value}>
                                                {item.label}
                                                <Select.ItemIndicator/>
                                            </Select.Item>
                                        ))}
                                    </Select.Content>
                                </Select.Positioner>
                            </Portal>
                        </Select.Root>
                    </Field.Root>


                    <Fieldset.Content>
                        <Field.Root mb={4}>
                            <Field.Label>Start Date</Field.Label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                max={endDate}
                            />
                        </Field.Root>

                        <Field.Root mb={4}>
                            <Field.Label>End Date</Field.Label>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                min={startDate}
                                max={formatDate(today)}
                            />
                        </Field.Root>

                        <Field.Root mb={4}>
                            <Field.Label>Visualization Mode</Field.Label>
                            <Select.Root
                                collection={visualizationModes}
                                value={mode}
                                onValueChange={(e) => setMode(e.value)}
                            >
                                <Select.HiddenSelect/>
                                <Select.Control>
                                    <Select.Trigger>
                                        <Select.ValueText placeholder="Select Visualization Mode"/>
                                    </Select.Trigger>
                                    <Select.IndicatorGroup>
                                        <Select.Indicator/>
                                    </Select.IndicatorGroup>
                                </Select.Control>
                                <Portal>
                                    <Select.Positioner>
                                        <Select.Content>
                                            {visualizationModes.items.map((item) => (
                                                <Select.Item item={item} key={item.value}>
                                                    {item.label}
                                                    <Select.ItemIndicator/>
                                                </Select.Item>
                                            ))}
                                        </Select.Content>
                                    </Select.Positioner>
                                </Portal>
                            </Select.Root>
                        </Field.Root>

                        {mode[0] === "Custom (choose RGB)" && (
                            <Box ml={4} mb={4}>
                                <Field.Root mb={2}>
                                    <Field.Label>Red Band</Field.Label>
                                    <Select.Root
                                        collection={bandCollection}
                                        value={customRedBand}
                                        onValueChange={(e) => setCustomRedBand(e.value)}
                                    >
                                        <Select.HiddenSelect/>
                                        <Select.Control>
                                            <Select.Trigger>
                                                <Select.ValueText placeholder="Select Red Band"/>
                                            </Select.Trigger>
                                            <Select.IndicatorGroup>
                                                <Select.Indicator/>
                                            </Select.IndicatorGroup>
                                        </Select.Control>
                                        <Portal>
                                            <Select.Positioner>
                                                <Select.Content>
                                                    {bandCollection.items.map((item) => (
                                                        <Select.Item item={item} key={item.value}>
                                                            {item.label}
                                                            <Select.ItemIndicator/>
                                                        </Select.Item>
                                                    ))}
                                                </Select.Content>
                                            </Select.Positioner>
                                        </Portal>
                                    </Select.Root>
                                </Field.Root>
                                <Field.Root mb={2}>
                                    <Field.Label>Green Band</Field.Label>
                                    <Select.Root
                                        collection={bandCollection}
                                        value={customGreenBand}
                                        onValueChange={(e) => setCustomGreenBand(e.value)}
                                    >
                                        <Select.HiddenSelect/>
                                        <Select.Control>
                                            <Select.Trigger>
                                                <Select.ValueText placeholder="Select Green Band"/>
                                            </Select.Trigger>
                                            <Select.IndicatorGroup>
                                                <Select.Indicator/>
                                            </Select.IndicatorGroup>
                                        </Select.Control>
                                        <Portal>
                                            <Select.Positioner>
                                                <Select.Content>
                                                    {bandCollection.items.map((item) => (
                                                        <Select.Item item={item} key={item.value}>
                                                            {item.label}
                                                            <Select.ItemIndicator/>
                                                        </Select.Item>
                                                    ))}
                                                </Select.Content>
                                            </Select.Positioner>
                                        </Portal>
                                    </Select.Root>
                                </Field.Root>
                                <Field.Root>
                                    <Field.Label>Blue Band</Field.Label>
                                    <Select.Root
                                        collection={bandCollection}
                                        value={customBlueBand}
                                        onValueChange={(e) => setCustomBlueBand(e.value)}
                                    >
                                        <Select.HiddenSelect/>
                                        <Select.Control>
                                            <Select.Trigger>
                                                <Select.ValueText placeholder="Select Blue Band"/>
                                            </Select.Trigger>
                                            <Select.IndicatorGroup>
                                                <Select.Indicator/>
                                            </Select.IndicatorGroup>
                                        </Select.Control>
                                        <Portal>
                                            <Select.Positioner>
                                                <Select.Content>
                                                    {bandCollection.items.map((item) => (
                                                        <Select.Item item={item} key={item.value}>
                                                            {item.label}
                                                            <Select.ItemIndicator/>
                                                        </Select.Item>
                                                    ))}
                                                </Select.Content>
                                            </Select.Positioner>
                                        </Portal>
                                    </Select.Root>
                                </Field.Root>
                            </Box>
                        )}
                    </Fieldset.Content>
                </Fieldset.Root>

                <ButtonGroup my={4} spacing={4}>
                    <Button
                        colorPalette="black"
                        variant="subtle"
                        onClick={startPreviewWebSocket}  // <-- NEW
                        loading={loadingPreviews}
                        loadingText="Generating Previews..."
                        isDisabled={!polygonGeoJSON}
                    >
                        <Icon as={FiImage}/> Generate Previews
                    </Button>
                </ButtonGroup>


                {previewImages.length > 0 && (
                    <Box mt={6}>
                        {Object.entries(groupImagesByMonthAndWeek(previewImages)).map(([month, weeks]) => (
                            <Box key={month} mb={8}>
                                <Heading size="lg" mb={4} color="gray.800">
                                    {month}
                                </Heading>

                                {Object.entries(weeks).map(([week, urls]) => (
                                    <Box key={week} mb={6}>
                                        <Heading size="md" mb={3} color="gray.700">
                                            {week}
                                        </Heading>
                                        <SimpleGrid columns={{base: 1, sm: 2, md: 3, lg: 4}} spacing={4}>
                                            {urls.map(({url, timestamp}, index) => (
                                                <Box
                                                    key={index}
                                                    borderWidth="1px"
                                                    borderRadius="lg"
                                                    overflow="hidden"
                                                    m={1}
                                                    // shadow=""
                                                >
                                                    <AspectRatio ratio={1} w="100%">
                                                        <Skeleton loading={imageLoadState[url] === "loaded"}>
                                                            {imageLoadState[url] === "error" ? (
                                                                <Flex align="center" justify="center" w="100%" h="100%">
                                                                    <Text color="red.500" fontSize="sm">
                                                                        Failed to load
                                                                    </Text>
                                                                </Flex>
                                                            ) : (
                                                                <Image
                                                                    src={
                                                                        imageCacheBust[url]
                                                                            ? `${url}?cb=${imageCacheBust[url]}`
                                                                            : url
                                                                    }
                                                                    alt={`Preview ${timestamp}`}
                                                                    objectFit="cover"
                                                                />
                                                            )}
                                                        </Skeleton>
                                                    </AspectRatio>
                                                </Box>
                                            ))}
                                        </SimpleGrid>
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {/*{ndviData && ndviData.length > 0 && (*/}
            {/*    <Box mt={6} bg="white" p={8} rounded="lg" maxW="5xl">*/}
            {/*        <Box>*/}
            {/*            /!* Header with chart title and button *!/*/}
            {/*            <HStack justify="space-between" mb={4}>*/}
            {/*                <Heading size="sm">NDVI Over Time</Heading>*/}
            {/*                /!*<Button*!/*/}
            {/*                /!*    size="sm"*!/*/}
            {/*                /!*    variant="outline"*!/*/}
            {/*                /!*    loading={loadingAnalysis}    // show spinner while fetching*!/*/}
            {/*                /!*    onClick={handleNDVIAnalysis}   // <-- calls backend*!/*/}
            {/*                /!*>*!/*/}
            {/*                /!*    NDVI Analysis*!/*/}
            {/*                /!*</Button>*!/*/}
            {/*            </HStack>*/}

            {/*            /!* Chart *!/*/}
            {/*            <Box height="350px">*/}
            {/*                <ResponsiveContainer width="100%" height="100%">*/}
            {/*                    <LineChart*/}
            {/*                        data={ndviData}*/}
            {/*                        margin={{top: 20, right: 30, left: 0, bottom: 0}}*/}
            {/*                    >*/}
            {/*                        <CartesianGrid strokeDasharray="3 3"/>*/}
            {/*                        <XAxis dataKey="timestamp" tickFormatter={(t) => t.slice(0, 10)}/>*/}
            {/*                        <YAxis domain={[0, 1]}/>*/}
            {/*                        <Tooltip/>*/}

            {/*                        /!* Line *!/*/}
            {/*                        <Line*/}
            {/*                            type="linear"       // straight line*/}
            {/*                            dataKey="ndvi"*/}
            {/*                            stroke="#82ca9d"*/}
            {/*                            strokeWidth={2}*/}
            {/*                            dot={{r: 3}}*/}
            {/*                        />*/}

            {/*                        /!* Shaded area under the line *!/*/}
            {/*                        <Area*/}
            {/*                            type="linear"*/}
            {/*                            dataKey="ndvi"*/}
            {/*                            stroke={false}*/}
            {/*                            fill="rgba(130, 202, 157, 0.2)"*/}
            {/*                        />*/}
            {/*                    </LineChart>*/}
            {/*                </ResponsiveContainer>*/}
            {/*            </Box>*/}

            {/*            /!* OpenAI analysis text *!/*/}
            {/*            {ndviAnalysisText && (*/}
            {/*                <Box mt={6} p={16} bg="white" rounded="md" borderWidth="1px">*/}
            {/*                    <Heading size="m" mb={2}>NDVI Analysis</Heading>*/}
            {/*                    <Text whiteSpace="pre-wrap">{ndviAnalysisText}</Text>*/}
            {/*                </Box>*/}
            {/*            )}*/}
            {/*        </Box>*/}
            {/*    </Box>*/}
            {/*)}*/}

            {ndviData && ndviData.length > 0 && (
                <Box mt={6} bg="white" p={8} rounded="lg" maxW="5xl" boxShadow="sm">
                    <VStack align="stretch" spacing={6}>

                        {/* ================= CHART SECTION ================= */}
                        <Box position="relative">

                            <HStack justify="space-between" mb={4}>
                                <Heading size="sm">NDVI Over Time</Heading>

                                {/*{loadingAnalysis && (*/}
                                {/*    <HStack spacing={2}>*/}
                                {/*        <Spinner size="sm" color="green.500"/>*/}
                                {/*        <Text fontSize="sm" color="gray.500">*/}
                                {/*            Analyzing vegetation trends...*/}
                                {/*        </Text>*/}
                                {/*    </HStack>*/}
                                {/*)}*/}
                            </HStack>

                            {/* Chart */}
                            <Box height="350px" opacity={loadingAnalysis ? 0.6 : 1} transition="0.2s">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={ndviData}
                                        margin={{top: 20, right: 30, left: 0, bottom: 0}}
                                    >
                                        <CartesianGrid strokeDasharray="3 3"/>
                                        <XAxis
                                            dataKey="timestamp"
                                            tickFormatter={(t) => t.slice(0, 10)}
                                        />
                                        <YAxis domain={[0, 1]}/>
                                        <Tooltip/>

                                        <Line
                                            type="linear"
                                            dataKey="ndvi"
                                            stroke="#82ca9d"
                                            strokeWidth={2}
                                            dot={{r: 3}}
                                        />

                                        <Area
                                            type="linear"
                                            dataKey="ndvi"
                                            stroke={false}
                                            fill="rgba(130, 202, 157, 0.2)"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </Box>

                        {/* ================= ANALYSIS SECTION ================= */}

                        {(loadingAnalysis || ndviAnalysisText) && (
                            <Box
                                mt={4}
                                p={8}
                                bg="white"
                                rounded="lg"
                                borderWidth="1px"
                            >
                                <Heading size="sm" mb={4}>
                                    NDVI Assessment
                                </Heading>

                                {loadingAnalysis && !ndviAnalysisText && (
                                    <VStack spacing={4} py={6}>
                                        <Spinner size="md" color="green.500"/>
                                        <Text fontSize="sm" color="gray.500">
                                            Generating agronomic insights from satellite data...
                                        </Text>
                                    </VStack>
                                )}

                                {ndviAnalysisText && (
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            h3: ({...props}) => (
                                                <Heading size="md" mt={6} mb={3} {...props} />
                                            ),
                                            h4: ({...props}) => (
                                                <Heading size="sm" mt={4} mb={2} {...props} />
                                            ),
                                            p: ({...props}) => (
                                                <Text mb={3} fontSize="sm" {...props} />
                                            ),

                                            // 👇 IMPORTANT CHANGE
                                            ul: ({children}) => (
                                                <Box
                                                    as="ul"
                                                    listStyleType="none" // Remove default browser bullets
                                                    ps={0}               // Reset padding-start
                                                    mb={4}
                                                    mt={2}
                                                >
                                                    {children}
                                                </Box>
                                            ),

                                            li: ({children}) => (
                                                <Box
                                                    as="li"
                                                    fontSize="sm"
                                                    position="relative"
                                                    ps={6} // Space for the custom bullet
                                                    mb={2}
                                                    _before={{
                                                        content: '""',
                                                        display: "inline-block",
                                                        position: "absolute",
                                                        left: 1,
                                                        top: "0.5em", // Vertically centers with the first line
                                                        width: "6px",
                                                        height: "6px",
                                                        borderRadius: "full",
                                                        bg: "black", // Your brand color here
                                                    }}
                                                >
                                                    {children}
                                                </Box>
                                            ),

                                            strong: ({...props}) => (
                                                <Text as="span" fontWeight="semibold" {...props} />
                                            ),
                                            hr: () => (
                                                <Box my={6} borderTop="1px solid" borderColor="gray.200"/>
                                            ),
                                        }}
                                    >
                                        {ndviAnalysisText}
                                    </ReactMarkdown>
                                )}
                            </Box>
                        )}

                    </VStack>
                </Box>
            )}
        </Box>
    );
};

export default DemoSatelliteImagery;